import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Download, Link as LinkIcon, RefreshCw, Trash2, CheckCircle, Calendar as CalendarIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CalendarSettings({ user, logout }) {
  const navigate = useNavigate();
  const [connectedCalendars, setConnectedCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [connectProvider, setConnectProvider] = useState("");
  const [calendarEmail, setCalendarEmail] = useState("");
  const [syncing, setSyncing] = useState("");

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const response = await axios.get(`${API}/calendar/connections`, {
        withCredentials: true
      });
      setConnectedCalendars(response.data.connected_calendars || []);
    } catch (error) {
      console.error("Error fetching connections:", error);
    } finally {
      setLoading(false);
    }
  };

  const downloadCalendar = async (type) => {
    try {
      const response = await axios.get(`${API}/calendar/export/${type}.ics`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `scc_football_${type}.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Calendar downloaded successfully");
    } catch (error) {
      console.error("Error downloading calendar:", error);
      toast.error("Failed to download calendar");
    }
  };

  const addToGoogleCalendar = () => {
    const url = `https://calendar.google.com/calendar/r?cid=webcal://${window.location.host}/api/calendar/export/combined.ics`;
    window.open(url, '_blank');
  };

  const addToOutlookCalendar = () => {
    const url = `${API}/calendar/export/combined.ics`;
    window.open(url, '_blank');
    toast.info("Open the downloaded file to add to Outlook");
  };

  const initiateOAuth = async (provider) => {
    try {
      const response = await axios.get(`${API}/calendar/oauth/${provider}/url`, {
        withCredentials: true
      });
      
      // Open OAuth flow in popup
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      const popup = window.open(
        response.data.auth_url,
        'oauth',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      
      // Listen for OAuth callback
      window.addEventListener('message', async (event) => {
        if (event.data.type === 'oauth-callback' && event.data.code) {
          popup.close();
          
          // Send auth code to backend
          try {
            await axios.post(
              `${API}/calendar/connect`,
              {
                provider: provider,
                auth_code: event.data.code,
                calendar_email: calendarEmail || user.email
              },
              { withCredentials: true }
            );
            
            toast.success(`${provider === 'microsoft' ? 'Outlook' : 'Google'} calendar connected!`);
            setShowConnectDialog(false);
            setCalendarEmail("");
            fetchConnections();
          } catch (error) {
            console.error("Connection error:", error);
            toast.error("Failed to connect calendar");
          }
        }
      });
      
    } catch (error) {
      console.error("OAuth error:", error);
      toast.error("Failed to initiate OAuth");
    }
  };

  const syncCalendar = async (provider) => {
    setSyncing(provider);
    try {
      await axios.post(
        `${API}/calendar/sync/${provider}`,
        {},
        { withCredentials: true }
      );
      toast.success(`${provider === 'microsoft' ? 'Outlook' : 'Google'} calendar synced!`);
      fetchConnections();
    } catch (error) {
      console.error("Sync error:", error);
      toast.error(error.response?.data?.detail || "Failed to sync calendar");
    } finally {
      setSyncing("");
    }
  };

  const disconnectCalendar = async (provider) => {
    if (!window.confirm(`Disconnect ${provider === 'microsoft' ? 'Outlook' : 'Google'} calendar?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/calendar/disconnect/${provider}`, {
        withCredentials: true
      });
      toast.success("Calendar disconnected");
      fetchConnections();
    } catch (error) {
      console.error("Disconnect error:", error);
      toast.error("Failed to disconnect calendar");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0f1a]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-500 mx-auto"></div>
          <p className="mt-4 text-slate-300 font-medium">Loading calendar settings...</p>
        </div>
      </div>
    );
  }

  const microsoftConnected = connectedCalendars.find(c => c.provider === 'microsoft');
  const googleConnected = connectedCalendars.find(c => c.provider === 'google');

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={() => navigate("/dashboard")}
            variant="ghost"
            className="text-yellow-500 hover:text-yellow-500"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <CalendarIcon className="w-8 h-8 text-yellow-500" />
          Calendar Integration
        </h1>
        <p className="text-slate-400 mb-6">
          Export SCC events to your calendar or import your schedule
        </p>

        <div className="space-y-6">
          {/* Export Section */}
          <Card className="p-6 bg-slate-900/50 border-2 border-white/5 shadow-lg">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Download className="w-5 h-5 text-yellow-500" />
              Export SCC Calendar
            </h2>
            <p className="text-sm text-slate-400 mb-4">
              Add SCC games and events to your Outlook or Google Calendar
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                onClick={addToGoogleCalendar}
                variant="outline"
                className="w-full"
              >
                <img src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg" alt="Google" className="w-5 h-5 mr-2" />
                Add to Google Calendar
              </Button>

              <Button
                onClick={addToOutlookCalendar}
                variant="outline"
                className="w-full"
              >
                <img src="https://upload.wikimedia.org/wikipedia/commons/d/df/Microsoft_Office_Outlook_%282018%E2%80%93present%29.svg" alt="Outlook" className="w-5 h-5 mr-2" />
                Add to Outlook
              </Button>
            </div>

            <div className="mt-4 pt-4 border-t border-white/5">
              <p className="text-sm text-slate-400 mb-3">Download .ics files:</p>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => downloadCalendar('games')}
                  size="sm"
                  variant="outline"
                >
                  Games Only
                </Button>
                <Button
                  onClick={() => downloadCalendar('events')}
                  size="sm"
                  variant="outline"
                >
                  Events Only
                </Button>
                <Button
                  onClick={() => downloadCalendar('combined')}
                  size="sm"
                  variant="outline"
                >
                  Combined
                </Button>
              </div>
            </div>
          </Card>

          {/* Import Section */}
          <Card className="p-6 bg-slate-900/50 border-2 border-white/5 shadow-lg">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-yellow-500" />
              Import Your Calendar (Auto-populate Availability)
            </h2>
            <p className="text-sm text-slate-400 mb-4">
              Connect your work/personal calendar to automatically mark when you're unavailable
            </p>

            <div className="space-y-4">
              {/* Microsoft Outlook */}
              <div className="p-4 bg-[#0a0f1a] rounded-lg border border-white/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img 
                      src="https://upload.wikimedia.org/wikipedia/commons/d/df/Microsoft_Office_Outlook_%282018%E2%80%93present%29.svg" 
                      alt="Outlook" 
                      className="w-8 h-8"
                    />
                    <div>
                      <h3 className="font-medium text-white">Microsoft Outlook</h3>
                      {microsoftConnected ? (
                        <>
                          <p className="text-sm text-emerald-400 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Connected: {microsoftConnected.calendar_email}
                          </p>
                          {microsoftConnected.last_synced && (
                            <p className="text-xs text-slate-400">
                              Last synced: {new Date(microsoftConnected.last_synced).toLocaleString()}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-slate-400">Not connected</p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {microsoftConnected ? (
                      <>
                        <Button
                          onClick={() => syncCalendar('microsoft')}
                          disabled={syncing === 'microsoft'}
                          size="sm"
                          variant="outline"
                        >
                          <RefreshCw className={`w-4 h-4 mr-2 ${syncing === 'microsoft' ? 'animate-spin' : ''}`} />
                          Sync Now
                        </Button>
                        <Button
                          onClick={() => disconnectCalendar('microsoft')}
                          size="sm"
                          variant="outline"
                          className="text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => {
                          setConnectProvider('microsoft');
                          setShowConnectDialog(true);
                        }}
                        size="sm"
                        className="bg-yellow-500/100 hover:bg-yellow-400 text-black"
                      >
                        Connect
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Google Calendar */}
              <div className="p-4 bg-[#0a0f1a] rounded-lg border border-white/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img 
                      src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg" 
                      alt="Google" 
                      className="w-8 h-8"
                    />
                    <div>
                      <h3 className="font-medium text-white">Google Calendar</h3>
                      {googleConnected ? (
                        <>
                          <p className="text-sm text-emerald-400 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Connected: {googleConnected.calendar_email}
                          </p>
                          {googleConnected.last_synced && (
                            <p className="text-xs text-slate-400">
                              Last synced: {new Date(googleConnected.last_synced).toLocaleString()}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-slate-400">Not connected</p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {googleConnected ? (
                      <>
                        <Button
                          onClick={() => syncCalendar('google')}
                          disabled={syncing === 'google'}
                          size="sm"
                          variant="outline"
                        >
                          <RefreshCw className={`w-4 h-4 mr-2 ${syncing === 'google' ? 'animate-spin' : ''}`} />
                          Sync Now
                        </Button>
                        <Button
                          onClick={() => disconnectCalendar('google')}
                          size="sm"
                          variant="outline"
                          className="text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => {
                          setConnectProvider('google');
                          setShowConnectDialog(true);
                        }}
                        size="sm"
                        className="bg-yellow-500/100 hover:bg-yellow-400 text-black"
                      >
                        Connect
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-slate-300">
                <strong>Note:</strong> Your calendar email can be different from your SCC email. 
                When you connect, we'll sync busy times from the next 3 months and automatically mark you as unavailable for those dates.
              </p>
            </div>
          </Card>
        </div>

        {/* Connect Dialog */}
        <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Connect {connectProvider === 'microsoft' ? 'Outlook' : 'Google'} Calendar</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Calendar Email (Optional)
                </label>
                <Input
                  type="email"
                  value={calendarEmail}
                  onChange={(e) => setCalendarEmail(e.target.value)}
                  placeholder={user.email}
                />
                <p className="text-xs text-slate-400 mt-1">
                  Leave blank to use your SCC email ({user.email})
                </p>
              </div>

              <div className="flex gap-2 justify-end">
                <Button
                  onClick={() => setShowConnectDialog(false)}
                  variant="ghost"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => initiateOAuth(connectProvider)}
                  className="bg-yellow-500/100 hover:bg-yellow-400 text-black"
                >
                  Continue to {connectProvider === 'microsoft' ? 'Microsoft' : 'Google'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
